import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  Box,
  Checkbox,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { makeStyles } from '@material-ui/styles';
import DialogDefault from '../../../components/DialogDefault/DialogDefault';
import SimilarityModal from '../../Nsir/SimilarityModal';

const STATUS = {
  VALID: 'VALID',
  DUPLICATE: 'DUPLICATE',
  ERROR: 'ERROR',
  DUPLICATED_ERROR: 'DUPLICATED_ERROR',
};
const SELECTABLE_STATUSES = [STATUS.VALID, STATUS.DUPLICATE];

const FILTERS = [
  { key: 'all', label: summary => `Todos (${summary.total})` },
  { key: STATUS.VALID, label: summary => `${summary.valid} válidos`, icon: <CheckCircleOutlineIcon style={{ color: '#4caf50' }} /> },
  { key: STATUS.DUPLICATE, label: summary => `${summary.duplicate} similares`, icon: <WarningIcon style={{ color: '#ff9800' }} /> },
  { key: STATUS.ERROR, label: summary => `${summary.error} erros`, icon: <ErrorOutlineIcon style={{ color: '#f44336' }} /> },
];

const useStyles = makeStyles(theme => ({
  filterChip: {
    fontWeight: 600,
    fontSize: 13,
  },
  filterBar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  filterBtn: {
    textTransform: 'none',
    minWidth: 80,
  },
  tableContainer: {
    maxHeight: 420,
    overflowY: 'auto',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  tableHeader: {
    '& th': {
      fontWeight: 600,
      backgroundColor: theme.palette.bar.grayMiddle,
      whiteSpace: 'nowrap',
    },
  },
  zebraRowEven: {
    backgroundColor: '#ffffff',
  },
  zebraRowOdd: {
    backgroundColor: '#f7f7f7',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  errorChip: {
    margin: theme.spacing(0.25),
    maxWidth: 280,
    height: 'auto',
    '& .MuiChip-label': {
      whiteSpace: 'normal',
      display: 'block',
      padding: theme.spacing(0.5, 1),
    },
  },
  errorsCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.25),
  },
  footer: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  iconValid: { color: theme.palette.success ? theme.palette.success.main : '#4caf50', fontSize: 18 },
  iconWarning: { color: theme.palette.warning ? theme.palette.warning.main : '#ff9800', fontSize: 18 },
  iconError: { color: theme.palette.error.main, fontSize: 18 },
  typeChipNew: {
    backgroundColor: theme.palette.info ? theme.palette.info.light : '#e3f2fd',
    color: theme.palette.info ? theme.palette.info.dark : '#1565c0',
    height: 20,
    fontSize: 11,
  },
  typeChipEdit: {
    backgroundColor: theme.palette.warning ? theme.palette.warning.light : '#fff3e0',
    color: theme.palette.warning ? theme.palette.warning.dark : '#e65100',
    height: 20,
    fontSize: 11,
  },
  errorMessage: {
    color: theme.palette.error ? theme.palette.error.dark : 'red',
    fontSize: 12,
  },
  fileName: {
    color: theme.palette.bar.principal,
    fontWeight: 600,
  },
  duplicateBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
  },
}));

const StatusIcon = ({ status, classes }) => {
  if (status === STATUS.VALID) return <CheckCircleOutlineIcon className={classes.iconValid} />;
  if (status === STATUS.DUPLICATE) return <WarningIcon className={classes.iconWarning} />;
  return <ErrorOutlineIcon className={classes.iconError} />;
};

const ImportValidationModal = ({
  open,
  preview,
  onCancel,
  onConfirm,
  onNewImport,
  importedFileName,
  onDownloadImportedFile,
}) => {
  const classes = useStyles();
  const [activeFilter, setActiveFilter] = useState('all');
  const [similarityRow, setSimilarityRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const rows = useMemo(() => (preview && Array.isArray(preview.rows) ? preview.rows : []), [preview]);
  const summary = preview ? preview.summary : { total: 0, valid: 0, duplicate: 0, error: 0 };
  const rowsWithIndex = useMemo(() => rows.map((row, rowIndex) => ({ row, rowIndex })), [rows]);

  const getRowKey = (row, idx) => `${row.line}-${idx}`;

  useEffect(() => {
    if (!open) return;
    if (rowsWithIndex.length > 0) {
      const defaultSelected = new Set();
      rowsWithIndex.forEach(({ row, rowIndex }) => {
        if (row.status === STATUS.VALID) {
          defaultSelected.add(getRowKey(row, rowIndex));
        }
      });
      setSelectedRows(defaultSelected);
      return;
    }
    setSelectedRows(new Set());
  }, [open, rowsWithIndex]);

  const filteredRows = useMemo(() => {
    if (activeFilter === 'all') return rowsWithIndex;
    return rowsWithIndex.filter(({ row }) => row.status === activeFilter);
  }, [rowsWithIndex, activeFilter]);

  const selectedRowsData = useMemo(
    () =>
      rowsWithIndex.reduce((acc, { row, rowIndex }) => {
        if (SELECTABLE_STATUSES.includes(row.status) && selectedRows.has(getRowKey(row, rowIndex))) {
          acc.push({ ...row, _rowIndex: rowIndex });
        }
        return acc;
      }, []),
    [rowsWithIndex, selectedRows]
  );
  const selectedLines = useMemo(
    () =>
      selectedRowsData
        .map(row => Number(row.line))
        .filter(Number.isInteger),
    [selectedRowsData]
  );

  const importCount = selectedLines.length;
  const confirmLabel = `Importar ${importCount} NSIR${importCount !== 1 ? 's' : ''}`;

  const handleRowSelect = (rowKey) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowKey)) {
      newSelected.delete(rowKey);
    } else {
      newSelected.add(rowKey);
    }
    setSelectedRows(newSelected);
  };

  return (
    <>
      <DialogDefault
        open={open}
        title="Revisão de Importação"
        handlerCancel={onCancel}
        handlerConfirm={() => onConfirm(selectedLines)}
        confirmMessage={confirmLabel}
        disabledConfirmButton={selectedLines.length === 0}
        cancelMessage="Cancelar"
        fullWidth
        maxWidth="lg"
        disableBackdropClick
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <div className={classes.filterBar}>
            {FILTERS.map(f => (
              <Chip
                key={f.key}
                className={classes.filterChip}
                icon={f.icon}
                label={f.label(summary)}
                onClick={() => setActiveFilter(f.key)}
                variant={activeFilter === f.key ? 'default' : 'outlined'}
                color={activeFilter === f.key ? 'primary' : 'default'}
              />
            ))}
          </div>
          <Box display="flex" alignItems="flex-start" gridGap={12}>
            {onNewImport && (
              <Button
                variant="contained"
                color="primary"
                onClick={onNewImport}
              >
                Importar planilha
              </Button>
            )}
            {(importedFileName || onDownloadImportedFile) && (
              <Box display="flex" flexDirection="column" alignItems="center" gridGap={4}>
                {onDownloadImportedFile && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onDownloadImportedFile}
                  >
                    Baixar planilha importada
                  </Button>
                )}
                {importedFileName && (
                  <Typography variant="caption" className={classes.fileName}>
                    {importedFileName}
                  </Typography>
                )}
              </Box>
            )}
            
          </Box>
        </Box>

        <div className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell padding="checkbox" style={{ width: 48 }} />
                <TableCell>Linha</TableCell>
                <TableCell>NSIR</TableCell>
                <TableCell>Equipamento</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Detalhes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map(({ row, rowIndex }, visibleIndex) => {
                const rowKey = getRowKey(row, rowIndex);
                const isSelected = selectedRows.has(rowKey);
                return (
                  <TableRow key={rowKey} hover className={visibleIndex % 2 === 0 ? classes.zebraRowEven : classes.zebraRowOdd}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleRowSelect(rowKey)}
                        size="small"
                        disabled={row.status === STATUS.ERROR || row.status === STATUS.DUPLICATED_ERROR}
                      />
                    </TableCell>
                    <TableCell>{`${row.line} (${row.isEdit ? 'Edição' : 'Novo'})`}</TableCell>
                    <TableCell>{row.nsir || '-'}</TableCell>
                    <TableCell>{row.equipamento || '-'}</TableCell>
                    <TableCell>
                      <Box className={classes.statusCell}>
                        <StatusIcon status={row.status} classes={classes} />
                        <Typography variant="caption">
                          {row.status === STATUS.VALID && 'Válido'}
                          {row.status === STATUS.DUPLICATE && 'Similaridade'}
                          {row.status === STATUS.ERROR && (<> Error <br /> (Informações incorretas) </>)}
                          {row.status === STATUS.DUPLICATED_ERROR && (<> Duplicado <br /> (NSIR e Equipamento) </>)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.status === STATUS.DUPLICATED_ERROR && <Button size="small" color="primary" onClick={() => setSimilarityRow(row)}> Ver informações </Button>}
                      {row.status === STATUS.DUPLICATE && <Button size="small" color="primary" onClick={() => setSimilarityRow(row)}> Ver similares </Button>}
                      {row.status === STATUS.ERROR && (
                        <div className={classes.errorsCell}>
                          {(row.errors || []).map((err, i) => (
                            <Chip key={i} size="small" label={err} className={classes.errorChip} style={{ backgroundColor: '#ffebee', color: '#c62828' }} />
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {summary.error > 0 && (
          <Typography variant="caption" className={classes.errorMessage}>
            *** Linhas com erro não podem ser processadas. É necessário atualizar a planilha e realizar uma nova importação
          </Typography>
        )}
      </DialogDefault>

      <SimilarityModal
        open={!!similarityRow}
        results={similarityRow ? (similarityRow.similar || []) : []}
        onCancel={() => setSimilarityRow(null)}
        onProceed={() => setSimilarityRow(null)}
        informationalOnly
      />
    </>
  );
};

ImportValidationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  preview: PropTypes.shape({
    summary: PropTypes.shape({
      total: PropTypes.number,
      valid: PropTypes.number,
      duplicate: PropTypes.number,
      error: PropTypes.number,
    }),
    rows: PropTypes.arrayOf(PropTypes.shape({
      line: PropTypes.number,
      nsir: PropTypes.string,
      equipamento: PropTypes.string,
      status: PropTypes.oneOf(['VALID', 'DUPLICATE', 'ERROR']),
      errors: PropTypes.arrayOf(PropTypes.string),
      similar: PropTypes.array,
      isEdit: PropTypes.bool,
    })),
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onNewImport: PropTypes.func,
  importedFileName: PropTypes.string,
  onDownloadImportedFile: PropTypes.func,
};

export default ImportValidationModal;
